import { themes } from '@/config/themes';
import type { ThemeConfig } from '@/types/theme';

/**
 * THE BOARD'S TWELVE PRESETS, MAPPED ONTO THE REAL CATALOGUE.
 *
 * `g_set_theme` draws six light chips and seven dark ones. Every one of them
 * is a theme that already ships in `src/config/themes.ts` — the labels are
 * shorter than the catalogue's names ("Slate", not "Slate Dark"; "Plum", not
 * "Royal Plum"), because on a screen whose two sections are already headed
 * "Light presets" and "Dark presets", repeating the word in every chip is
 * noise. The ID is what is saved, so the label is presentation and nothing
 * else — a preset renamed in the catalogue keeps working here.
 *
 * A preset whose id is missing from the catalogue is dropped rather than drawn
 * as a chip that cannot be applied.
 */

export interface Preset {
  id: string;
  /** The chip's label, as the board writes it. */
  label: string;
  /**
   * The line the docked pane prints under the preset's name.
   *
   * WHY NOT THE CATALOGUE'S OWN `description`. The pane is 360px wide and the
   * sub is one line; the catalogue's descriptions are written for a settings
   * list twice that wide ("The previous MAIE palette — indigo primary, beige
   * accent"), so the pane ellipsised them mid-word. The board writes its own
   * line instead — "the palette the product ships with" under MAIE Brand — and
   * these are that line, one per preset, authored to the pane's width.
   */
  sub: string;
  theme: ThemeConfig;
}

const byId = new Map(themes.map((t) => [t.id, t]));

function build(triples: Array<[string, string, string]>): Preset[] {
  return triples
    .map(([id, label, sub]) => {
      const theme = byId.get(id);
      return theme ? { id, label, sub, theme } : null;
    })
    .filter((p): p is Preset => p !== null);
}

export const LIGHT_PRESETS = build([
  ['periwinkle', 'MAIE Brand', 'the palette the product ships with'],
  ['maei-brand', 'Classic', 'the palette MAIE shipped before this one'],
  ['medical-green', 'Medical Green', 'green, for a clinic that wants calm'],
  ['professional-purple', 'Professional Purple', 'purple, a quieter kind of formal'],
  ['high-contrast', 'High Contrast', 'the most readable pair, for tired eyes'],
  ['custom', 'Custom', 'your own hexes, in the slots below'],
]);

export const DARK_PRESETS = build([
  ['periwinkle-dark', 'MAIE Brand Dark', 'the shipped palette, after dark'],
  ['indigo-night', 'Indigo Night', 'the brand indigo on a night canvas'],
  ['slate-dark', 'Slate', 'grey on near-black, the neutral dark'],
  ['emerald-dark', 'Emerald', 'the calm green, after dark'],
  ['royal-dark', 'Plum', 'the quiet purple, after dark'],
  ['carbon-contrast', 'Carbon', 'the most readable pair, on near-black'],
  ['custom-dark', 'Custom', 'your own hexes, for the dark half'],
]);

export function presetOf(id: string | null | undefined): Preset | null {
  if (!id) return null;
  return LIGHT_PRESETS.find((p) => p.id === id)
    ?? DARK_PRESETS.find((p) => p.id === id)
    ?? null;
}

/**
 * The dark half the light half pairs with, when the tenant has not chosen one.
 * The board's pane says "Pairs with MAIE Brand Dark after dark", and a light
 * preset with no dark counterpart set still has to name what the device will
 * paint after sunset — the catalogue's default dark.
 */
export const DEFAULT_DARK_ID = 'periwinkle-dark';
